import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { AppleSignIn } from '@capawesome/capacitor-apple-sign-in';
import * as Sentry from '@sentry/react';
import { Core, setAuthToken, httpClient } from '@/api';
import { saveAuthSession, clearAuthSession, restoreSessionToLocalStorage } from '@/features/core/auth/authStorage';
import { tokenStorage } from '@/core/services/tokenStorage';
import { initializePushNotifications } from '@/features/core/services/pushNotifications';
import { getApiErrorMessage } from '@/lib/api-errors';
import { loginWithPasskey } from '@/lib/webauthn';
import i18n from '@/i18n';

export function isAppleCancelError(error) {
  if (!error) return false;
  const msg = (error.message || error.toString() || '').toLowerCase();
  const code = error.code || error.errorCode;
  return (
    code === 1001 ||
    code === '1001' ||
    code === 'SIGN_IN_CANCELED' ||
    msg.includes('canceled') ||
    msg.includes('cancelled') ||
    msg.includes('1001')
  );
}

/**
 * Synchronizes user identity to Sentry for session attribution and error tracing.
 * Clears user context with null when unauthenticated.
 */
function setSentryUserContext(userProfile) {
  if (!userProfile) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: userProfile.id || userProfile.userId,
    email: userProfile.email,
    role: userProfile.role,
    ...(userProfile.organizationId || userProfile.organization_id
      ? { organization_id: userProfile.organizationId || userProfile.organization_id }
      : {}),
  });
}

export function buildSocialOAuthUrl({ provider, clientId, redirectUri, state }) {
  if (provider === 'facebook') {
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      scope: 'email,public_profile',
      response_type: 'code',
      state,
    });
    return `https://www.facebook.com/v21.0/dialog/oauth?${params.toString()}`;
  }
  if (provider === 'instagram') {
    const params = new URLSearchParams({
      enable_fb_login: '0',
      force_authentication: '1',
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'instagram_business_basic',
      state,
    });
    return `https://www.instagram.com/oauth/authorize?${params.toString()}`;
  }
  return null;
}

/** @type {import('react').Context<any>} */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Synchronize user context to Sentry whenever authentication state changes
  useEffect(() => {
    setSentryUserContext(user);
  }, [user]);

  useEffect(() => {
    const hydrateUser = async () => {
      try {
        await restoreSessionToLocalStorage();
      } catch (err) {
        console.error('Error during secure preference restoration:', err);
      }

      // Check for token in URL query parameter (diagnostic impersonation handoff)
      const currentPath = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
      const isPublicVerificationRoute =
        currentPath.startsWith('/accept-invitation') ||
        currentPath.startsWith('/magic-link') ||
        currentPath.startsWith('/auth/callback') ||
        currentPath.startsWith('/data-deletion') ||
        currentPath.startsWith('/public/') ||
        currentPath.startsWith('/sign/') ||
        currentPath.startsWith('/homysign/');

      let urlToken = null;
      let urlRefreshToken = null;
      if (typeof window !== 'undefined' && !isPublicVerificationRoute) {
        const searchParams = new URLSearchParams(window.location.search);
        urlToken = searchParams.get('token') || searchParams.get('impersonation_token');
        urlRefreshToken = searchParams.get('refreshToken') || searchParams.get('refresh_token');
        if (urlToken) {
          tokenStorage.setAccessToken(urlToken);
          if (urlRefreshToken) {
            tokenStorage.setRefreshToken(urlRefreshToken);
          } else {
            tokenStorage.setRefreshToken(null);
          }
          // Clean token from address bar to prevent leaking in history
          searchParams.delete('token');
          searchParams.delete('impersonation_token');
          searchParams.delete('refreshToken');
          searchParams.delete('refresh_token');
          const remainingQuery = searchParams.toString();
          const cleanUrl = window.location.pathname + (remainingQuery ? `?${remainingQuery}` : '') + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }

      const storedUser = urlToken ? null : tokenStorage.getUser();
      const storedToken = urlToken || tokenStorage.getAccessToken();

      if (!storedToken) {
        setLoading(false);
        return;
      }

      setAuthToken(storedToken);

      if (storedUser) {
        setUser(storedUser);
        setSentryUserContext(storedUser);
      }

      try {
        const currentUser = await Core.api.getCurrentUserInfo();
        setUser(currentUser);
        setSentryUserContext(currentUser);
        tokenStorage.setUser(currentUser);
        await saveAuthSession(storedToken, currentUser);
        initializePushNotifications();
      } catch {
        tokenStorage.clearTokens();
        await clearAuthSession();
        setAuthToken(null);
        setUser(null);
        setSentryUserContext(null);
      } finally {
        setLoading(false);
      }
    };

    hydrateUser();
  }, []);

  const loginWithApple = useCallback(async (options = {}) => {
    try {
      setError(null);
      const isNative = Capacitor.isNativePlatform();

      let identityToken = options.identityToken;
      let authorizationCode = options.authorizationCode;
      let givenName = options.givenName;
      let familyName = options.familyName;
      let email = options.email;

      // If credentials were not passed in options, acquire via native or show desktop notice
      if (!identityToken) {
        if (!isNative) {
          const desktopMessage = i18n.t(
            'auth:apple.desktopNotice',
            { defaultValue: 'Apple Sign-In is configured for the iOS mobile app. On desktop web, please use Email, Passkey, or Magic Link.' }
          );
          setError(desktopMessage);
          return { unsupportedPlatform: true };
        }

        try {
          const result = await AppleSignIn.signIn({
            clientId: 'com.heningroup.homy',
            scopes: ['email', 'name'],
          });
          identityToken = result.idToken || result.identityToken;
          authorizationCode = result.authorizationCode;
          givenName = result.givenName;
          familyName = result.familyName;
          email = result.email;
        } catch (nativeErr) {
          if (isAppleCancelError(nativeErr)) {
            // User intentionally canceled native sheet: suppress error silently
            return null;
          }
          const message = nativeErr?.response
            ? getApiErrorMessage(nativeErr, i18n.t('auth:authFailed'))
            : nativeErr?.message || getApiErrorMessage(nativeErr, i18n.t('auth:authFailed'));
          setError(message);
          throw new Error(message);
        }
      }

      if (!identityToken) {
        return null;
      }

      const payload = {
        identityToken,
        authorizationCode: authorizationCode || undefined,
        guestSessionToken: options.guestSessionToken || undefined,
        userData: (givenName || familyName || email) ? {
          firstName: givenName || undefined,
          lastName: familyName || undefined,
          email: email || undefined,
        } : undefined,
      };

      const response = await httpClient.instance.post('/api/core/auth/apple', payload);
      const data = response.data;

      if (data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          mfaMethod: data.mfaMethod || 'TOTP',
        };
      }

      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;

      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const apiMessage = err?.response
        ? i18n.t('auth:apiError', { message: getApiErrorMessage(err, i18n.t('auth:authFailed')) })
        : err?.message || i18n.t('auth:apiError', { message: getApiErrorMessage(err, i18n.t('auth:authFailed')) });
      setError(apiMessage);
      throw new Error(apiMessage);
    }
  }, [queryClient]);

  const loginLocal = useCallback(async (userData) => {
    try {
      setError(null);
      const payload = typeof userData === 'string' ? { email: userData } : userData;
      const response = await Core.api.authenticateLocal(payload);
      const { token, refreshToken } = response;
      tokenStorage.setAccessToken(token);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(token);

      // Drop stale cache from a previous session so every query refetches
      // with the new token — prevents serving outdated/empty data.
      queryClient.clear();

      // AuthResponse omits organizationSlug — fetch the full profile so
      // downstream consumers (e.g. share banner) have all fields immediately.
      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(token, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);

      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:authFailed'));
      const apiMessage = i18n.t('auth:apiError', { message });
      setError(apiMessage);
      throw new Error(apiMessage);
    }
  }, [queryClient]);

  const loginWithPassword = useCallback(async ({ email, password, guestSessionToken }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/login', {
        email,
        password,
        guestSessionToken,
      });
      const data = response.data;
      if (data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          mfaMethod: data.mfaMethod || 'TOTP',
        };
      }

      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:authFailed'));
      const apiMessage = i18n.t('auth:apiError', { message });
      setError(apiMessage);
      throw new Error(apiMessage);
    }
  }, [queryClient]);

  const signupWithPassword = useCallback(async ({ email, password, fullName, organizationName, countryCode = 'CL', guestSessionToken }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/signup', {
        email,
        password,
        fullName,
        organizationName,
        countryCode,
        guestSessionToken,
      });
      const data = response.data;
      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:authFailed'));
      const apiMessage = i18n.t('auth:apiError', { message });
      setError(apiMessage);
      throw new Error(apiMessage);
    }
  }, [queryClient]);

  const loginWithPasskeyAuth = useCallback(async (guestSessionToken) => {
    try {
      setError(null);
      const data = await loginWithPasskey(guestSessionToken);
      if (data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          mfaMethod: data.mfaMethod || 'TOTP',
        };
      }

      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:passkey.failed', { defaultValue: 'Passkey authentication failed' }));
      setError(message);
      throw new Error(message);
    }
  }, [queryClient]);

  const verifyMfaChallenge = useCallback(async ({ mfaToken, code }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/mfa/challenge/verify', {
        mfaToken,
        code,
      });
      const data = response.data;
      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:mfa.invalidCode', { defaultValue: 'Invalid verification code' }));
      throw new Error(message);
    }
  }, [queryClient]);

  const verifyMfaBackupCode = useCallback(async ({ mfaToken, backupCode }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/mfa/backup-code/verify', {
        mfaToken,
        backupCode,
      });
      const data = response.data;
      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:mfa.invalidBackupCode', { defaultValue: 'Invalid backup code' }));
      throw new Error(message);
    }
  }, [queryClient]);

  const loginWithSocial = useCallback(async ({
    provider,
    accessToken,
    credential,
    code,
    redirectUri,
    guestSessionToken,
    clientId,
    redirectAfterAuth,
  }) => {
    try {
      setError(null);
      const hasDirectCredential = Boolean(accessToken || credential || code);

      if (!hasDirectCredential && (provider === 'facebook' || provider === 'instagram')) {
        let resolvedClientId =
          clientId ||
          (provider === 'facebook'
            ? import.meta.env.VITE_FACEBOOK_CLIENT_ID
            : import.meta.env.VITE_INSTAGRAM_CLIENT_ID);

        if (!resolvedClientId && httpClient?.instance?.get) {
          try {
            const providersRes = await httpClient.instance.get('/api/core/auth/providers');
            const statusData = providersRes?.data || {};
            resolvedClientId =
              provider === 'facebook'
                ? statusData.facebookClientId || statusData.facebook_client_id
                : statusData.instagramClientId || statusData.instagram_client_id;
          } catch {
            // Fall through to missing client ID error below
          }
        }

        if (!resolvedClientId) {
          throw new Error(
            i18n.t('auth:socialProviderNotConfigured', {
              defaultValue: `${provider.charAt(0).toUpperCase() + provider.slice(1)} Login is not configured on this environment.`,
            })
          );
        }

        const origin = typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : 'http://127.0.0.1:3010';
        const callbackUri = redirectUri || `${origin}/auth/callback/${provider}`;
        const stateNonce = `${provider}_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
        let currentReturnCandidate = '/';
        if (typeof window !== 'undefined' && window.location?.pathname) {
          const curPath = window.location.pathname;
          const curSearch = window.location.search || '';
          if (
            curPath.startsWith('/login') ||
            curPath.startsWith('/signup') ||
            curPath.startsWith('/auth/callback')
          ) {
            const sp = new URLSearchParams(curSearch);
            currentReturnCandidate = sp.get('redirect') || '/';
          } else {
            currentReturnCandidate = `${curPath}${curSearch}`;
          }
        }
        const returnTo = redirectAfterAuth || currentReturnCandidate;

        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(
            'homy_oauth_state',
            JSON.stringify({
              nonce: stateNonce,
              provider,
              redirectUri: callbackUri,
              returnTo,
              guestSessionToken: guestSessionToken || null,
            })
          );
        }

        const authUrl = buildSocialOAuthUrl({
          provider,
          clientId: resolvedClientId,
          redirectUri: callbackUri,
          state: stateNonce,
        });

        if (typeof window !== 'undefined' && authUrl) {
          if (typeof window.location?.assign === 'function') {
            window.location.assign(authUrl);
          } else if (window.location) {
            window.location.href = authUrl;
          }
        }
        return { redirecting: true, authUrl };
      }

      const endpoint = `/api/core/auth/social/${provider}`;
      const payload = { guestSessionToken };
      if (provider === 'google') {
        payload.credential = credential || accessToken;
      } else {
        if (accessToken || credential) {
          payload.accessToken = accessToken || credential;
        }
        if (code) {
          payload.code = code;
        }
        if (redirectUri) {
          payload.redirectUri = redirectUri;
        }
      }
      const response = await httpClient.instance.post(endpoint, payload);
      const data = response.data;
      if (data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          mfaMethod: data.mfaMethod || 'TOTP',
        };
      }
      const { token, accessToken: newAccessToken, refreshToken } = data;
      const jwtToken = token || newAccessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, err?.message || i18n.t('auth:authFailed'));
      setError(message);
      throw new Error(message);
    }
  }, [queryClient]);

  const sendMessagingOtp = useCallback(async ({ phone, channel = 'whatsapp' }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/messaging/send-otp', {
        phone,
        channel,
      });
      return response.data;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:otpSendFailed', { defaultValue: 'Failed to send OTP' }));
      throw new Error(message);
    }
  }, []);

  const verifyMessagingOtp = useCallback(async ({ phone, code, channel = 'whatsapp', fullName, guestSessionToken }) => {
    try {
      setError(null);
      const response = await httpClient.instance.post('/api/core/auth/messaging/verify-otp', {
        phone,
        code,
        channel,
        fullName,
        guestSessionToken,
      });
      const data = response.data;
      if (data.mfaRequired) {
        return {
          mfaRequired: true,
          mfaToken: data.mfaToken,
          mfaMethod: data.mfaMethod || 'TOTP',
        };
      }
      const { token, accessToken, refreshToken } = data;
      const jwtToken = token || accessToken;
      tokenStorage.setAccessToken(jwtToken);
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      setAuthToken(jwtToken);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(jwtToken, fullUser, refreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:otpVerifyFailed', { defaultValue: 'Invalid OTP code' }));
      throw new Error(message);
    }
  }, [queryClient]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const logout = useCallback(() => {
    tokenStorage.clearTokens();
    clearAuthSession();
    setAuthToken(null);
    setUser(null);
    setError(null);
    setSentryUserContext(null);
    // Purge all cached queries to prevent leaking data between sessions.
    queryClient.clear();
  }, [queryClient]);

  const refreshSession = useCallback(async () => {
    try {
      const storedRefreshToken = tokenStorage.getRefreshToken();
      const response = await Core.api.refreshToken({ refreshToken: storedRefreshToken });
      const { token, refreshToken: newRefreshToken } = response;
      tokenStorage.setAccessToken(token);
      if (newRefreshToken) {
        tokenStorage.setRefreshToken(newRefreshToken);
      }
      setAuthToken(token);

      // Same as loginLocal — fetch full profile for organizationSlug etc.
      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(token, fullUser, newRefreshToken || storedRefreshToken);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:authFailed'));
      throw new Error(message);
    }
  }, []);

  const loginWithToken = useCallback(async (token) => {
    try {
      setError(null);
      tokenStorage.setAccessToken(token);
      tokenStorage.setRefreshToken(null);
      setAuthToken(token);
      queryClient.clear();

      const fullUser = await Core.api.getCurrentUserInfo();
      tokenStorage.setUser(fullUser);
      await saveAuthSession(token, fullUser);
      initializePushNotifications();
      setUser(fullUser);
      setSentryUserContext(fullUser);
      return fullUser;
    } catch (err) {
      const message = getApiErrorMessage(err, i18n.t('auth:authFailed'));
      const apiMessage = i18n.t('auth:apiError', { message });
      setError(apiMessage);
      throw new Error(apiMessage);
    }
  }, [queryClient]);

  const endImpersonation = useCallback(() => {
    tokenStorage.clearTokens();
    clearAuthSession();
    setAuthToken(null);
    setUser(null);
    setSentryUserContext(null);
    queryClient.clear();
    const platformBase = import.meta.env.VITE_PLATFORM_URL ||
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://127.0.0.1:3030'
        : 'https://platform.homyos.app');
    const platformUrl = `${platformBase.replace(/\/$/, '')}/overview`;
    window.location.href = platformUrl;
  }, [queryClient]);

  const loginWithFacebook = useCallback(
    (options = {}) => loginWithSocial({ ...options, provider: 'facebook' }),
    [loginWithSocial]
  );

  const loginWithInstagram = useCallback(
    (options = {}) => loginWithSocial({ ...options, provider: 'instagram' }),
    [loginWithSocial]
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      loading,
      error,
      clearError,
      isAuthenticated: !!user,
      loginWithApple,
      loginLocal,
      loginWithPassword,
      signupWithPassword,
      loginWithPasskey: loginWithPasskeyAuth,
      verifyMfaChallenge,
      verifyMfaBackupCode,
      loginWithSocial,
      loginWithFacebook,
      loginWithInstagram,
      sendMessagingOtp,
      verifyMessagingOtp,
      loginWithToken,
      logout,
      endImpersonation,
      refreshSession,
    }),
    [
      user,
      setUser,
      loading,
      error,
      clearError,
      loginWithApple,
      loginLocal,
      loginWithPassword,
      signupWithPassword,
      loginWithPasskeyAuth,
      verifyMfaChallenge,
      verifyMfaBackupCode,
      loginWithSocial,
      loginWithFacebook,
      loginWithInstagram,
      sendMessagingOtp,
      verifyMessagingOtp,
      loginWithToken,
      logout,
      endImpersonation,
      refreshSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * @returns {any}
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
export default AuthContext;

